import { toast } from "react-toastify";
import CabinetsApi from "../services/api/cabinetsApi";
import HistoryApi from "../services/api/historyApi";
import { formatedRemainingTime, hasPlanFeature } from "../utils";
import axiosInstance from "../services/instance/axiosInstance";

/**
 * Download file with progress tracking
 * @param {string} filePath - File path to download
 * @param {function} setDownloading - State setter for loading state
 * @param {object} translations - Translation object with keys like 'starting_download', 'downloading', etc.
 * @param {string} archiveId - Archive ID for history tracking
 * @returns {Promise<void>}
 */
export const handleFileDownload = async (
  filePath,
  setDownloading,
  translations,
  archiveId
) => {
  try {
    setDownloading(true);
    const toastId = toast.info(translations.starting_download, {
      autoClose: false,
      closeButton: false,
      progress: 0,
    });

    // Get download URL
    const downloadUrlResponse = await CabinetsApi.downloadArchive(filePath);

    if (!downloadUrlResponse?.success) {
      toast.update(toastId, {
        render: translations.download_failed_try_again,
        type: "error",
        autoClose: 2000,
      });
      return;
    }

    // Download file with progress tracking
    const fileName = decodeURIComponent(filePath).split("/").pop();
    const startTime = Date.now();

    const fileResponse = await axiosInstance.post('/download-file', { file_path: filePath }, {
      responseType: "blob",
      onDownloadProgress: (progressEvent) => {
        if (progressEvent.lengthComputable) {
          const { loaded, total } = progressEvent;
          const percentCompleted = Math.round((loaded / total) * 100);
          const elapsedTime = (Date.now() - startTime) / 1000;
          const speed = loaded / elapsedTime;
          const remainingBytes = total - loaded;
          const estimatedTimeRemaining = remainingBytes / speed;
          const formattedETA = formatedRemainingTime(estimatedTimeRemaining);

          toast.update(toastId, {
            render: () => (
              <div className="d-flex flex-column">
                <div className="d-flex gap-1">
                  <strong>{translations.downloading}:</strong>
                  <span>{`${percentCompleted}%`}</span>
                </div>
                <div className="d-flex gap-1">
                  <span>{translations.remaining_time}:</span>
                  <span dir="ltr">{formattedETA}</span>
                </div>
              </div>
            ),
            progress: percentCompleted / 100,
          });
        }
      },
    });

    // Trigger download
    const blob = new Blob([fileResponse.data]);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    toast.update(toastId, {
      render: translations.download_complete,
      type: "success",
      autoClose: 2000,
      progress: null,
    });

    // Create history record
    if (hasPlanFeature(1) && archiveId) {
      await HistoryApi.createHistory({
        archive_id: archiveId,
        action_type: "download",
        accessed_at: new Date().toLocaleString(),
      });
    }
  } catch (error) {
    console.error("Download error:", error);
    toast.error(
      translations.download_failed_try_again || "Download failed. Please try again."
    );
  } finally {
    setDownloading(false);
  }
};

/**
 * Preview file in modal
 * @param {string} filePath - File path to preview
 * @param {function} setViewingFile - State setter for viewing file
 * @param {function} setShowModal - State setter for showing modal
 * @param {function} setDownloading - State setter for loading state
 * @param {object} translations - Translation object
 * @param {string} archiveId - Archive ID for history tracking
 * @returns {Promise<void>}
 */
export const handleFilePreview = async (
  filePath,
  setViewingFile,
  setShowModal,
  setDownloading,
  translations,
  archiveId
) => {
  try {
    setDownloading(true);
    // const fileName = decodeURIComponent(filePath).split("/").pop();
    // const fileType = fileName.split(".").pop()?.toLowerCase();

    const response = await axiosInstance.post(
      `${process.env.REACT_APP_API_BASE_URL}/preview-file`,
      { file_path: filePath },
      { responseType: "blob" } // Axios will wrap ANY response in a Blob
    );

    // 1. Check if the response is actually JSON (an error) instead of a file
    if (response.data.type === "application/json") {
      // Convert blob back to text to read the error message
      const text = await response.data.text();
      const errorData = JSON.parse(text);
      throw new Error(errorData.message || "Backend error");
    }

    // 2. Create the URL with the correct MIME type from headers
    const blob = new Blob([response.data], { type: response.headers['content-type'] });
    const previewUrl = URL.createObjectURL(blob);
    const fileType = filePath.split(".").pop()?.toLowerCase();

    setViewingFile({ url: previewUrl, type: fileType });
    setShowModal(true);

    // Create history record
    if (hasPlanFeature(1) && archiveId) {
    await HistoryApi.createHistory({
        archive_id: archiveId,
        action_type: "view",
        accessed_at: new Date().toLocaleString(),
    });
    }
  } catch (error) {
    console.error("Preview error:", error);
    toast.error(
      translations.preview_failed_try_again || "Preview failed. Please try again."
    );
  } finally {
    setDownloading(false);
  }
};

/**
 * Clean up blob URL when component unmounts
 * @param {string} blobUrl - Blob URL to revoke
 */
export const revokeBlobUrl = (blobUrl) => {
  if (blobUrl) {
    URL.revokeObjectURL(blobUrl);
  }
};

/**
 * Get file extension from path
 * @param {string} filePath - File path
 * @returns {string} - File extension
 */
export const getFileExtension = (filePath) => {
  const fileName = decodeURIComponent(filePath).split("/").pop();
  return fileName.split(".").pop()?.toLowerCase() || "";
};

/**
 * Get file name from path
 * @param {string} filePath - File path
 * @returns {string} - File name
 */
export const getFileName = (filePath) => {
  return decodeURIComponent(filePath).split("/").pop();
};

/**
 * Check if file type is previewable (image or pdf)
 * @param {string} fileType - File type/extension
 * @returns {boolean} - True if previewable
 */
export const isPreviewable = (fileType) => {
  const previewableTypes = ["jpg", "jpeg", "png", "gif", "webp", "pdf"];
  return previewableTypes.includes(fileType?.toLowerCase());
};
